<template>
  <section id="waitlist" class="waitlist-form">
    <div class="form-container glass-card">
      <div class="form-header">
        <span class="badge">Exclusive Beta</span>
        <h2>Help Us Build Bizzento</h2>
        <p class="subtitle">
          Share your challenges and we'll tailor Bizzento to solve them.
        </p>
      </div>

      <Transition name="fade-slide" mode="out-in">
        <form
          v-if="!showSuccess && formData"
          @submit.prevent="handleSubmit"
          id="waitlistForm"
          class="actual-form"
        >
          <div class="form-group">
            <label for="business"
              >What business do you run? <span class="required">*</span></label
            >
            <input
              type="text"
              id="business"
              v-model="formData.business"
              required
              placeholder="e.g., Home bakery, Online boutique"
            />
          </div>

          <div class="form-group">
            <label for="topQuestion"
              >What's the #1 question you'd want Bizzento to answer?
              <span class="required">*</span></label
            >
            <div class="select-wrapper">
              <select id="topQuestion" v-model="formData.topQuestion" required>
                <option value="" disabled selected>Select an option</option>
                <option value="pricing">Should I raise my prices?</option>
                <option value="profitable">
                  Which products are most profitable?
                </option>
                <option value="hiring">When should I hire help?</option>
                <option value="scale">Am I ready to scale?</option>
                <option value="demand">What are customers asking for?</option>
                <option value="marketing">
                  Where should I focus my marketing?
                </option>
                <option value="other">Other (please specify)</option>
              </select>
            </div>
          </div>

          <Transition name="expand">
            <div class="form-group" v-if="formData.topQuestion === 'other'">
              <label for="otherQuestion">Please specify your question</label>
              <textarea
                id="otherQuestion"
                v-model="formData.otherQuestion"
                :required="formData.topQuestion === 'other'"
                placeholder="What would you most want to know?"
              ></textarea>
            </div>
          </Transition>

          <div class="form-group">
            <label for="email"
              >Email Address <span class="required">*</span></label
            >
            <input
              type="email"
              id="email"
              v-model="formData.email"
              required
              placeholder="name@example.com"
            />
          </div>

          <div v-if="errorMessage" class="error-message">
            {{ errorMessage }}
          </div>

          <button type="submit" class="submit-button" :disabled="isSubmitting">
            <span v-if="!isSubmitting">Join the Beta Program</span>
            <span v-else class="loader-text">Submitting...</span>
          </button>

          <p class="privacy-note">🔒 We value your privacy. No spam, ever.</p>
        </form>

        <div v-else class="success-message">
          <div class="success-icon">✨</div>
          <h3>You're on the list!</h3>
          <p>
            Thank you for joining our journey. We'll be in touch soon to discuss
            how Bizzento can help your business thrive.
          </p>
          <button @click="showSuccess = false" class="back-btn">Done</button>
        </div>
      </Transition>
    </div>
  </section>
</template>
<script setup>
import { ref, reactive, nextTick } from 'vue';

const API_ENDPOINT = 'https://services.mviaso.com/api/email/waitlist';

// Reactive Form Data
const formData = reactive({
  business: '',
  topQuestion: '',
  otherQuestion: '',
  email: '',
});

const isSubmitting = ref(false);
const showSuccess = ref(false);
const errorMessage = ref('');

const handleSubmit = async () => {
  isSubmitting.value = true;
  errorMessage.value = '';

  const submitData = {
    business: formData.business,
    topQuestion: formData.topQuestion,
    otherQuestion:
      formData.topQuestion === 'other' ? formData.otherQuestion : '',
    email: formData.email,
    timestamp: new Date().toISOString(),
  };

  try {
    const response = await fetch(API_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(submitData),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `Server error: ${response.status}`);
    }

    showSuccess.value = true;

    // Reset form fields
    formData.business = '';
    formData.topQuestion = '';
    formData.otherQuestion = '';
    formData.email = '';

    await nextTick();
    document.querySelector('.success-message')?.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
    });
  } catch (error) {
    console.error('Form submission error:', error);
    errorMessage.value =
      error.message || 'Something went wrong. Please try again.';
  } finally {
    isSubmitting.value = false;
  }
};
</script>
<style scoped>
.waitlist-form {
  --bg-warm-base: #f2eee0;
  --soft-gold: #f5e5b0;
  --dark-accent: #1a1a1a;
  --text-muted: #706c64;
  --glass-bg: rgba(255, 255, 255, 0.8);

  padding: 100px 24px;
  background-color: var(--bg-warm-base);
  display: flex;
  justify-content: center;
  align-items: center;
}

.form-container {
  width: 100%;
  max-width: 560px;
  background: var(--glass-bg);
  backdrop-filter: blur(15px);
  -webkit-backdrop-filter: blur(15px);
  border: 1px solid rgba(255, 255, 255, 0.7);
  padding: 60px 45px;
  border-radius: 32px;
  box-shadow: 0 30px 60px -12px rgba(0, 0, 0, 0.05);
  text-align: center;
}

.form-header {
  margin-bottom: 40px;
}

.badge {
  background: var(--soft-gold);
  color: #634e12;
  padding: 8px 16px;
  border-radius: 100px;
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

h2 {
  font-size: 2.2rem;
  margin: 24px 0 12px;
  font-weight: 800;
  color: var(--dark-accent);
}

.subtitle {
  color: var(--text-muted);
  font-size: 1.1rem;
  line-height: 1.5;
}

/* Alignment Fixes */
.form-group {
  margin-bottom: 24px;
  text-align: left; /* Ensures labels and inputs align left */
}

label {
  display: block;
  font-weight: 600;
  margin-bottom: 8px;
  font-size: 0.95rem;
  color: var(--dark-accent);
}

.required {
  color: #d97706;
}

input,
select,
textarea {
  width: 100%;
  padding: 16px 18px;
  border-radius: 12px;
  border: 1.2px solid rgba(0, 0, 0, 0.1);
  background: #ffffff;
  font-size: 1rem;
  transition: all 0.2s ease;
  box-sizing: border-box; /* Crucial for alignment */
}

input:focus,
select:focus,
textarea:focus {
  outline: none;
  border-color: var(--soft-gold);
  box-shadow: 0 0 0 4px rgba(245, 229, 176, 0.4);
}

/* Custom Select Styling */
.select-wrapper {
  position: relative;
}

.select-wrapper::after {
  content: '▼';
  font-size: 0.7rem;
  position: absolute;
  right: 18px;
  top: 50%;
  transform: translateY(-50%);
  pointer-events: none;
  opacity: 0.5;
}

select {
  appearance: none;
  cursor: pointer;
}

.submit-button {
  width: 100%;
  background: var(--dark-accent);
  color: white;
  padding: 18px;
  border: none;
  border-radius: 14px;
  font-size: 1.1rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-top: 10px;
}

.submit-button:hover:not(:disabled) {
  transform: translateY(-2px);
  background: #000;
}

.submit-button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.privacy-note {
  margin-top: 24px;
  font-size: 0.85rem;
  color: var(--text-muted);
}

/* Transitions */
.fade-slide-enter-active,
.fade-slide-leave-active {
  transition: all 0.4s ease;
}
.fade-slide-enter-from {
  opacity: 0;
  transform: translateY(15px);
}
.fade-slide-leave-to {
  opacity: 0;
  transform: translateY(-15px);
}

.expand-enter-active,
.expand-leave-active {
  transition: all 0.3s ease;
  max-height: 150px;
  overflow: hidden;
}
.expand-enter-from,
.expand-leave-to {
  max-height: 0;
  opacity: 0;
}

.error-message {
  color: #c53030;
  background: #fff5f5;
  padding: 12px;
  border-radius: 8px;
  margin-bottom: 20px;
  font-size: 0.9rem;
  border: 1px solid #feb2b2;
}

.success-message h3 {
  font-size: 2rem;
  margin-bottom: 16px;
}

.back-btn {
  margin-top: 32px;
  background: transparent;
  border: 1.5px solid var(--soft-gold);
  padding: 10px 32px;
  border-radius: 100px;
  cursor: pointer;
  font-weight: 600;
}

@media (max-width: 600px) {
  .form-container {
    padding: 40px 24px;
    border-radius: 20px;
  }
}
</style>
